<div class="min-h-screen bg-base-200/50 flex flex-col lg:flex-row">
  <!-- Mobile Backdrop -->
  <div
    class="lg:hidden fixed inset-0 z-50 bg-black/50 transition-opacity"
    [class.hidden]="!sidebarOpen"
    (click)="toggleMobileSidebar()"
  ></div>

  <!-- Sidebar Container -->
  <aside
    class="fixed lg:sticky top-0 left-0 h-screen h-[100dvh] max-h-[100dvh] bg-base-100 border-r border-base-300 shadow-lg lg:shadow-none z-[55] lg:z-30 flex flex-col transition-all duration-300 shrink-0"
    [class.w-64]="!desktopCollapsed"
    [class.w-20]="desktopCollapsed"
    [class.overflow-visible]="desktopCollapsed"
    [class.overflow-hidden]="!desktopCollapsed"
    [class.translate-x-0]="sidebarOpen"
    [class.-translate-x-full]="!sidebarOpen"
    [class.lg:translate-x-0]="true"
  >
    <!-- Sidebar Header: Brand Logo -->
    <div
      class="h-14 px-4 border-b border-base-300 flex items-center justify-between shrink-0"
    >
      @if (!desktopCollapsed) {
        <a
          routerLink="/"
          class="flex items-center gap-2 max-w-[190px] overflow-hidden"
        >
          @if (tenantLogoUrl) {
            <img
              [src]="tenantLogoUrl"
              [alt]="currentTenant?.name || 'Academy Logo'"
              class="h-8 max-w-full object-contain"
            />
          } @else if (isDefaultTenant) {
            <img
              src="assets/images/logo-single.png"
              alt="Learnbird Logo"
              class="h-7"
            />
            <img
              src="assets/images/logo-text.png"
              alt="Learnbird Logo"
              class="h-5"
            />
          } @else {
            <span
              class="font-bold text-lg text-base-content tracking-tight truncate max-w-[190px]"
              >{{ currentTenant?.name }}</span
            >
          }
        </a>
      } @else {
        <a
          routerLink="/"
          class="flex items-center justify-center w-full daisy-tooltip daisy-tooltip-right z-[60]"
          [attr.data-tip]="currentTenant?.name || 'Learnbird'"
          [title]="currentTenant?.name || 'Learnbird'"
        >
          @if (tenantLogoUrl) {
            <img
              [src]="tenantLogoUrl"
              [alt]="currentTenant?.name || 'Academy Logo'"
              class="h-7 w-7 object-contain"
            />
          } @else {
            <img
              src="assets/images/logo-single.png"
              alt="Learnbird Logo"
              class="h-7"
            />
          }
        </a>
      }

      <button
        type="button"
        class="lg:hidden daisy-btn daisy-btn-ghost daisy-btn-sm daisy-btn-square"
        (click)="toggleMobileSidebar()"
        aria-label="Close sidebar"
      >
        <i class="fa-solid fa-xmark text-lg"></i>
      </button>
    </div>

    <!-- Navigation Menu -->
    <nav
      class="flex-1 min-h-0 p-2"
      [class.overflow-visible]="desktopCollapsed"
      [class.overflow-y-auto]="!desktopCollapsed"
      [class.overflow-x-hidden]="!desktopCollapsed"
    >
      @if (!desktopCollapsed) {
        <!-- Expanded Menu -->
        <ul class="daisy-menu daisy-menu-sm w-full gap-1">
          <li>
            <a
              routerLink="/dashboard/overview"
              routerLinkActive="active"
              class="flex items-center gap-3 rounded-lg"
            >
              <i class="fa-solid fa-gauge w-5 text-center"></i>
              <span>Overview</span>
            </a>
          </li>
          <li>
            <a
              routerLink="/live-classes"
              routerLinkActive="active"
              class="flex items-center gap-3 rounded-lg"
            >
              <i class="fa-solid fa-compass w-5 text-center"></i>
              <span>Explore Classes</span>
            </a>
          </li>
          @if (showCourses) {
            <li>
              <a
                routerLink="/courses"
                routerLinkActive="active"
                [routerLinkActiveOptions]="{ exact: true }"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-book-open-reader w-5 text-center"></i>
                <span>Explore Courses</span>
              </a>
            </li>
          }

          @if (isInstructorOrAdmin) {
            <li
              class="daisy-menu-title text-xs uppercase tracking-wider mt-3 mb-1 px-3 opacity-50"
            >
              Teaching
            </li>
            <li>
              <a
                routerLink="/dashboard/instructor"
                routerLinkActive="active"
                [routerLinkActiveOptions]="{ exact: false }"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-person-chalkboard w-5 text-center"></i>
                <span>Manage Classes</span>
              </a>
            </li>
            @if (showCourses) {
              <li>
                <a
                  routerLink="/dashboard/courses"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i class="fa-solid fa-graduation-cap w-5 text-center"></i>
                  <span>Manage Courses</span>
                </a>
              </li>
            }
            @if (showTutorProfile) {
              <li>
                <a
                  routerLink="/dashboard/profile/tutor-profile"
                  routerLinkActive="active"
                  class="flex items-center justify-between rounded-lg"
                >
                  <div class="flex items-center gap-3">
                    <div class="relative">
                      <i class="fa-solid fa-id-card w-5 text-center"></i>
                      @if (hasIncompleteTutorProfile) {
                        <span
                          class="absolute -top-1 -right-1 flex h-2.5 w-2.5"
                          title="Your tutor profile is incomplete. Click to create or update your profile."
                        >
                          <span
                            class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"
                          ></span>
                          <span
                            class="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"
                          ></span>
                        </span>
                      }
                    </div>
                    <span>Tutor Profile</span>
                  </div>
                </a>
              </li>
            }
            <li>
              <a
                routerLink="/dashboard/app-management"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-puzzle-piece w-5 text-center"></i>
                <span>Integrations</span>
              </a>
            </li>

            <li>
              <a
                routerLink="/dashboard/earnings"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-money-bill-trend-up w-5 text-center"></i>
                <span>Earnings</span>
              </a>
            </li>

            <li
              class="daisy-menu-title text-xs uppercase tracking-wider mt-3 mb-1 px-3 opacity-50"
            >
              Account
            </li>
            <li>
              <a
                routerLink="/dashboard/orders"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-receipt w-5 text-center"></i>
                <span>Orders</span>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/subscriptions"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-credit-card w-5 text-center"></i>
                <span>Subscriptions</span>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/invoices"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-file-invoice w-5 text-center"></i>
                <span>Invoices</span>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/profile"
                routerLinkActive="active"
                [routerLinkActiveOptions]="{ exact: true }"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-user w-5 text-center"></i>
                <span>My Profile</span>
              </a>
            </li>

            @if (!isTenantOwner) {
              <li
                class="daisy-menu-title text-xs uppercase tracking-wider mt-3 mb-1 px-3 opacity-50"
              >
                Learning
              </li>
              <li>
                <a
                  routerLink="/dashboard/my-batches"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i class="fa-solid fa-users-rectangle w-5 text-center"></i>
                  <span>My Batches</span>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/learning"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i class="fa-solid fa-chalkboard-user w-5 text-center"></i>
                  <span>My Classes</span>
                </a>
              </li>
              @if (showCourses) {
                <li>
                  <a
                    routerLink="/dashboard/my-courses"
                    routerLinkActive="active"
                    class="flex items-center gap-3 rounded-lg"
                  >
                    <i class="fa-solid fa-graduation-cap w-5 text-center"></i>
                    <span>My Courses</span>
                  </a>
                </li>
              }
              <li>
                <a
                  routerLink="/dashboard/meetings"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i class="fa-solid fa-calendar-check w-5 text-center"></i>
                  <span>Meetings</span>
                </a>
              </li>
            }
          } @else {
            <li
              class="daisy-menu-title text-xs uppercase tracking-wider mt-3 mb-1 px-3 opacity-50"
            >
              Learning
            </li>
            <li>
              <a
                routerLink="/dashboard/my-batches"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-users-rectangle w-5 text-center"></i>
                <span>My Batches</span>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/learning"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-chalkboard-user w-5 text-center"></i>
                <span>My Classes</span>
              </a>
            </li>
            @if (showCourses) {
              <li>
                <a
                  routerLink="/dashboard/my-courses"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i class="fa-solid fa-graduation-cap w-5 text-center"></i>
                  <span>My Courses</span>
                </a>
              </li>
            }
            <li>
              <a
                routerLink="/dashboard/meetings"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-calendar-check w-5 text-center"></i>
                <span>Meetings</span>
              </a>
            </li>

            <li
              class="daisy-menu-title text-xs uppercase tracking-wider mt-3 mb-1 px-3 opacity-50"
            >
              Account
            </li>
            <li>
              <a
                routerLink="/dashboard/orders"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-receipt w-5 text-center"></i>
                <span>Orders</span>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/subscriptions"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-credit-card w-5 text-center"></i>
                <span>Subscriptions</span>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/invoices"
                routerLinkActive="active"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-file-invoice w-5 text-center"></i>
                <span>Invoices</span>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/profile"
                routerLinkActive="active"
                [routerLinkActiveOptions]="{ exact: true }"
                class="flex items-center gap-3 rounded-lg"
              >
                <i class="fa-solid fa-user w-5 text-center"></i>
                <span>My Profile</span>
              </a>
            </li>
          }

          @if (isTenantOwner || isInstructor) {
            <li
              class="daisy-menu-title text-xs uppercase tracking-wider mt-3 mb-1 px-3 opacity-50"
            >
              Workspace
            </li>

            @if (isTenantOwner) {
              <li>
                <a
                  routerLink="/dashboard/students"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i class="fa-solid fa-user-graduate w-5 text-center"></i>
                  <span>Students</span>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/batches"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i class="fa-solid fa-users-rectangle w-5 text-center"></i>
                  <span>Batches</span>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/payments"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i
                    class="fa-solid fa-money-bill-transfer w-5 text-center"
                  ></i>
                  <span>Payments</span>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/workspace-usage"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i class="fa-solid fa-chart-pie w-5 text-center"></i>
                  <span>Usage</span>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/academy-settings"
                  routerLinkActive="active"
                  class="flex items-center gap-3 rounded-lg"
                >
                  <i class="fa-solid fa-sliders w-5 text-center"></i>
                  <span>{{ workspaceSettingsLabel }}</span>
                </a>
              </li>
            }
          }
        </ul>
      } @else {
        <!-- Collapsed Mini Icon Menu (Desktop only) -->
        <ul class="hidden lg:flex flex-col items-center gap-2 py-2">
          <li>
            <a
              routerLink="/dashboard/overview"
              routerLinkActive="bg-primary text-primary-content"
              class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
              data-tip="Overview"
              title="Overview"
            >
              <i class="fa-solid fa-gauge text-base text-center"></i>
            </a>
          </li>
          <li>
            <a
              routerLink="/live-classes"
              routerLinkActive="bg-primary text-primary-content"
              class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
              data-tip="Explore Classes"
              title="Explore Classes"
            >
              <i class="fa-solid fa-compass text-base text-center"></i>
            </a>
          </li>
          @if (showCourses) {
            <li>
              <a
                routerLink="/courses"
                routerLinkActive="bg-primary text-primary-content"
                [routerLinkActiveOptions]="{ exact: true }"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Explore Courses"
                title="Explore Courses"
              >
                <i
                  class="fa-solid fa-book-open-reader text-base text-center"
                ></i>
              </a>
            </li>
          }

          @if (isInstructorOrAdmin) {
            <div class="w-8 h-px bg-base-300 my-1"></div>
            <li>
              <a
                routerLink="/dashboard/instructor"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="My Classes"
                title="My Classes"
              >
                <i
                  class="fa-solid fa-person-chalkboard text-base text-center"
                ></i>
              </a>
            </li>
            @if (showCourses) {
              <li>
                <a
                  routerLink="/dashboard/courses"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="Manage Courses"
                  title="Manage Courses"
                >
                  <i
                    class="fa-solid fa-graduation-cap text-base text-center"
                  ></i>
                </a>
              </li>
            }
            @if (showTutorProfile) {
              <li>
                <a
                  routerLink="/dashboard/profile/tutor-profile"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 relative daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="Tutor Profile"
                  title="Tutor Profile"
                >
                  <i class="fa-solid fa-id-card text-base text-center"></i>
                  @if (hasIncompleteTutorProfile) {
                    <span class="absolute top-1 right-1 flex h-2 w-2">
                      <span
                        class="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"
                      ></span>
                      <span
                        class="relative inline-flex rounded-full h-2 w-2 bg-amber-500"
                      ></span>
                    </span>
                  }
                </a>
              </li>
            }
            <li>
              <a
                routerLink="/dashboard/app-management"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Integrations"
                title="Integrations"
              >
                <i class="fa-solid fa-puzzle-piece text-base text-center"></i>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/earnings"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Earnings"
                title="Earnings"
              >
                <i
                  class="fa-solid fa-money-bill-trend-up text-base text-center"
                ></i>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/orders"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Orders"
                title="Orders"
              >
                <i class="fa-solid fa-receipt text-base text-center"></i>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/subscriptions"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Subscriptions"
                title="Subscriptions"
              >
                <i class="fa-solid fa-credit-card text-base text-center"></i>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/invoices"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Invoices"
                title="Invoices"
              >
                <i class="fa-solid fa-file-invoice text-base text-center"></i>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/profile"
                routerLinkActive="bg-primary text-primary-content"
                [routerLinkActiveOptions]="{ exact: true }"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="My Profile"
                title="My Profile"
              >
                <i class="fa-solid fa-user text-base text-center"></i>
              </a>
            </li>
            @if (!isTenantOwner) {
              <div class="w-8 h-px bg-base-300 my-1"></div>
              <li>
                <a
                  routerLink="/dashboard/learning"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="My Classes"
                  title="My Classes"
                >
                  <i
                    class="fa-solid fa-chalkboard-user text-base text-center"
                  ></i>
                </a>
              </li>
              @if (showCourses) {
                <li>
                  <a
                    routerLink="/dashboard/my-courses"
                    routerLinkActive="bg-primary text-primary-content"
                    class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                    data-tip="My Courses"
                    title="My Courses"
                  >
                    <i
                      class="fa-solid fa-graduation-cap text-base text-center"
                    ></i>
                  </a>
                </li>
              }
              <li>
                <a
                  routerLink="/dashboard/my-batches"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="My Batches"
                  title="My Batches"
                >
                  <i
                    class="fa-solid fa-users-rectangle text-base text-center"
                  ></i>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/my-batches"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="My Batches"
                  title="My Batches"
                >
                  <i
                    class="fa-solid fa-users-rectangle text-base text-center"
                  ></i>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/meetings"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="Meetings"
                  title="Meetings"
                >
                  <i
                    class="fa-solid fa-calendar-check text-base text-center"
                  ></i>
                </a>
              </li>
            }
          } @else {
            <div class="w-8 h-px bg-base-300 my-1"></div>
            <li>
              <a
                routerLink="/dashboard/learning"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="My Classes"
                title="My Classes"
              >
                <i
                  class="fa-solid fa-chalkboard-user text-base text-center"
                ></i>
              </a>
            </li>
            @if (showCourses) {
              <li>
                <a
                  routerLink="/dashboard/my-courses"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="My Courses"
                  title="My Courses"
                >
                  <i
                    class="fa-solid fa-graduation-cap text-base text-center"
                  ></i>
                </a>
              </li>
            }
            <li>
              <a
                routerLink="/dashboard/meetings"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Meetings"
                title="Meetings"
              >
                <i class="fa-solid fa-calendar-check text-base text-center"></i>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/orders"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Orders"
                title="Orders"
              >
                <i class="fa-solid fa-receipt text-base text-center"></i>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/subscriptions"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Subscriptions"
                title="Subscriptions"
              >
                <i class="fa-solid fa-credit-card text-base text-center"></i>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/invoices"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Invoices"
                title="Invoices"
              >
                <i class="fa-solid fa-file-invoice text-base text-center"></i>
              </a>
            </li>
            <li>
              <a
                routerLink="/dashboard/profile"
                routerLinkActive="bg-primary text-primary-content"
                [routerLinkActiveOptions]="{ exact: true }"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="My Profile"
                title="My Profile"
              >
                <i class="fa-solid fa-user text-base text-center"></i>
              </a>
            </li>
          }

          @if (isTenantOwner || isInstructor) {
            <div class="w-8 h-px bg-base-300 my-1"></div>
            <li>
              <a
                routerLink="/dashboard/workspace-usage"
                routerLinkActive="bg-primary text-primary-content"
                class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                data-tip="Workspace Usage"
                title="Workspace Usage"
              >
                <i class="fa-solid fa-chart-pie text-base text-center"></i>
              </a>
            </li>
            @if (isTenantOwner) {
              <li>
                <a
                  routerLink="/dashboard/students"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="Students"
                  title="Students"
                >
                  <i
                    class="fa-solid fa-user-graduate text-base text-center"
                  ></i>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/batches"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="Batches"
                  title="Batches"
                >
                  <i
                    class="fa-solid fa-users-rectangle text-base text-center"
                  ></i>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/payments"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  data-tip="Payments"
                  title="Payments"
                >
                  <i
                    class="fa-solid fa-money-bill-transfer text-base text-center"
                  ></i>
                </a>
              </li>
              <li>
                <a
                  routerLink="/dashboard/academy-settings"
                  routerLinkActive="bg-primary text-primary-content"
                  class="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60]"
                  [attr.data-tip]="workspaceSettingsLabel"
                  [attr.title]="workspaceSettingsLabel"
                >
                  <i class="fa-solid fa-sliders text-base text-center"></i>
                </a>
              </li>
            }
          }
        </ul>
      }
    </nav>

    @if (!desktopCollapsed) {
      <!-- Sidebar Footer (Expanded) -->
      <div
        class="px-4 py-3 pb-5 border-t border-base-300 flex flex-col gap-1 shrink-0 bg-base-100"
      >
        <a
          routerLink="/"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm gap-2 justify-start text-xs"
        >
          <i class="fa-solid fa-arrow-left"></i>
          Back to Site
        </a>
        <button
          (click)="logout()"
          class="daisy-btn daisy-btn-ghost daisy-btn-sm gap-2 justify-start text-xs text-error hover:bg-error/10"
        >
          <i class="fa-solid fa-arrow-right-from-bracket"></i>
          Sign Out
        </button>
      </div>
    } @else {
      <!-- Sidebar Footer (Collapsed) -->
      <div
        class="hidden lg:flex py-3 border-t border-base-300 flex-col items-center gap-2 shrink-0"
      >
        <a
          routerLink="/"
          class="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-base-200 daisy-tooltip daisy-tooltip-right z-[60] text-xs"
          data-tip="Back to Site"
          title="Back to Site"
        >
          <i class="fa-solid fa-arrow-left text-base-content/80"></i>
        </a>
        <button
          (click)="logout()"
          class="w-9 h-9 rounded-lg flex items-center justify-center text-error hover:bg-error/10 daisy-tooltip daisy-tooltip-right z-[60] text-xs"
          data-tip="Sign Out"
          title="Sign Out"
        >
          <i class="fa-solid fa-arrow-right-from-bracket"></i>
        </button>
      </div>
    }
  </aside>

  <!-- Right Content Column: Header + Outlet -->
  <div class="flex-1 flex flex-col min-w-0">
    <black-dashboard-header></black-dashboard-header>
    <main class="flex-1 p-4 lg:p-6 overflow-y-auto">
      <router-outlet></router-outlet>
    </main>
  </div>
</div>
